import i18n from '@/plugins/i18n';

type DynamicTranslation = {
  pattern: RegExp;
  key: string;
  getParams?: (match: RegExpExecArray) => Record<string, unknown>;
};

const dynamicTranslations: DynamicTranslation[] = [
  {
    pattern: /^fila\s+excel$/i,
    key: 'response_error.import_excel_row',
  },
  {
    pattern: /^excel\s+row$/i,
    key: 'response_error.import_excel_row',
  },
  {
    pattern: /^error\s+al\s+leer\s+la\s+fila\.?$/i,
    key: 'response_error.import_read_row',
  },
  {
    pattern: /^error\s+reading\s+row\.?$/i,
    key: 'response_error.import_read_row',
  },
  {
    pattern: /^la\s+columna\s+(.+?)\s+no\s+se\s+ha\s+podido\s+convertir\s+a\s+fecha\.?$/i,
    key: 'response_error.import_column_date',
    getParams: (match) => ({ column: match[1] }),
  },
  {
    pattern: /^la\s+columna\s+(.+?)\s+no\s+se\s+ha\s+podido\s+convertir\s+a\s+n[uú]mero\.?$/i,
    key: 'response_error.import_column_number',
    getParams: (match) => ({ column: match[1] }),
  },
  {
    pattern: /^la\s+columna\s+(.+?)\s+est[aá]\s+vac[ií]a\.?$/i,
    key: 'response_error.import_column_empty',
    getParams: (match) => ({ column: match[1] }),
  },
  {
    pattern: /^column\s+(.+?)\s+could\s+not\s+be\s+converted\s+to\s+a\s+date\.?$/i,
    key: 'response_error.import_column_date',
    getParams: (match) => ({ column: match[1] }),
  },
  {
    pattern: /^column\s+(.+?)\s+could\s+not\s+be\s+converted\s+to\s+a\s+number\.?$/i,
    key: 'response_error.import_column_number',
    getParams: (match) => ({ column: match[1] }),
  },
  {
    pattern: /^column\s+(.+?)\s+is\s+empty\.?$/i,
    key: 'response_error.import_column_empty',
    getParams: (match) => ({ column: match[1] }),
  },
  {
    pattern: /^formato\s+de\s+d[ií]a\s+no\s+reconocido\.?$/i,
    key: 'response_error.import_invalid_day_format',
  },
  {
    pattern: /^formato\s+de\s+hora\s+no\s+reconocido\.?$/i,
    key: 'response_error.import_invalid_hour_format',
  },
  {
    pattern: /^unrecognized\s+day\s+format\.?$/i,
    key: 'response_error.import_invalid_day_format',
  },
  {
    pattern: /^unrecognized\s+hour\s+format\.?$/i,
    key: 'response_error.import_invalid_hour_format',
  },
  {
    pattern: /^no\s+existe\s+configuraci[oó]n\s+para\s+el\s+producto\s+indicado\.?$/i,
    key: 'response_error.product_configuration_not_found',
  },
  {
    pattern: /^no\s+product\s+configuration\s+exists\s+for\s+the\s+specified\s+product\.?$/i,
    key: 'response_error.product_configuration_not_found',
  },
];

function normalizeTranslationKey(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\s/]+/g, '_')
    .replace(/__+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
}

export function tUi(value: string, params?: Record<string, unknown>): string {
  const text = String(value || '').trim();

  if (!text) {
    return '';
  }

  if (i18n.te(text)) {
    return i18n.t(text, params).toString();
  }

  const commonText = `common.${text}`;
  if (i18n.te(commonText)) {
    return i18n.t(commonText, params).toString();
  }

  const normalizedText = normalizeTranslationKey(text);
  if (normalizedText !== text && i18n.te(normalizedText)) {
    return i18n.t(normalizedText, params).toString();
  }

  const normalizedCommonText = `common.${normalizedText}`;
  if (normalizedText !== text && i18n.te(normalizedCommonText)) {
    return i18n.t(normalizedCommonText, params).toString();
  }

  for (const rule of dynamicTranslations) {
    const match = rule.pattern.exec(text);
    if (!match || !i18n.te(rule.key)) {
      continue;
    }

    const dynamicParams = rule.getParams ? rule.getParams(match) : undefined;
    return i18n.t(rule.key, dynamicParams).toString();
  }

  return text;
}

export function mapTranslatedList<T extends { name: string }>(list: T[]): T[] {
  return list.map((item) => ({
    ...item,
    name: tUi(item.name),
  }));
}